import { useCallback, useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { AlertCircle, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useUpdateConcept } from "@/hooks/useConcepts";
import {
  useRefineConceptMutation,
  type RefineProposal,
} from "@/hooks/useConceptRefine";
import type { Concept } from "@/types/concept";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  concept: Concept;
  onAccepted: (basedOnVersionId: string | null) => void;
}

type ModalState = "loading" | "proposal" | "error";

const MAX_RETRIES = 2;

export function RefineConceptModal({
  open,
  onOpenChange,
  concept,
  onAccepted,
}: Props) {
  const [state, setState] = useState<ModalState>("loading");
  const [proposal, setProposal] = useState<RefineProposal | null>(null);
  const [editedName, setEditedName] = useState("");
  const [editedKeywords, setEditedKeywords] = useState("");
  const [retryCount, setRetryCount] = useState(0);
  const retriesRef = useRef(0);

  const refineMutation = useRefineConceptMutation(concept.id);
  const updateConcept = useUpdateConcept(concept.id);

  const proposalEditor = useEditor({
    extensions: [StarterKit],
    content: "<p></p>",
    editorProps: {
      attributes: {
        class:
          "min-h-[120px] max-h-[240px] overflow-y-auto rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring prose prose-sm max-w-none",
      },
    },
  });

  // Read-only render of the CURRENT description. This was
  // `dangerouslySetInnerHTML` until the 2026-07-28 audit (HIGH-3): the field is
  // written both by the user and by the AI refine service (which reads
  // untrusted paper abstracts), and `GET /api/concepts/{id}` honours
  // `X-Act-As-User` — so an admin impersonating a user rendered that user's
  // markup inside the admin's own authenticated session. Tiptap parses through
  // the StarterKit schema, which has no node for script/img/iframe, so active
  // markup cannot survive into the DOM.
  const currentEditor = useEditor({
    extensions: [StarterKit],
    content: concept.abstract || "<p><em>No description</em></p>",
    editable: false,
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none rounded-md border border-input bg-muted/50 px-3 py-2 text-sm max-h-[240px] overflow-y-auto",
      },
    },
  });

  useEffect(() => {
    currentEditor?.commands.setContent(
      concept.abstract || "<p><em>No description</em></p>",
    );
  }, [concept.abstract, currentEditor]);

  const fetchProposal = useCallback(() => {
    setState("loading");
    refineMutation.mutate(undefined, {
      onSuccess: (data) => {
        setProposal(data);
        setEditedName(data.name);
        setEditedKeywords(data.keywords.join(", "));
        proposalEditor?.commands.setContent(data.abstract || "<p></p>");
        setState("proposal");
      },
      onError: () => {
        setState("error");
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutate is stable
  }, [refineMutation.mutate, proposalEditor]);

  // Fetch when dialog opens
  useEffect(() => {
    if (open) {
      retriesRef.current = 0;
      setRetryCount(0);
      fetchProposal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on open change
  }, [open]);

  const handleRetry = useCallback(() => {
    if (retriesRef.current < MAX_RETRIES) {
      retriesRef.current += 1;
      setRetryCount(retriesRef.current);
      fetchProposal();
    }
  }, [fetchProposal]);

  const handleAccept = useCallback(() => {
    if (!proposal) return;

    // No `?? proposal.abstract` fallback: that persisted the model's raw string
    // when the editor wasn't ready, bypassing the only thing that constrains it
    // to the StarterKit schema. If there's no editor there's nothing safe to
    // save, so bail (audit 2026-07-28, HIGH-3).
    const abstractHtml = proposalEditor?.getHTML();
    if (abstractHtml == null) return;

    updateConcept.mutate(
      {
        name: editedName,
        abstract: abstractHtml,
        keywords: editedKeywords,
        change_source: "ai_refine",
        based_on_concept_updated_at: proposal.based_on_concept_updated_at,
      },
      {
        onSuccess: () => {
          onAccepted(proposal.based_on_concept_updated_at);
          onOpenChange(false);
        },
        onError: (err) => {
          const status = (err as { response?: { status?: number } })?.response
            ?.status;
          if (status === 409) {
            // Version conflict — regenerate proposal
            fetchProposal();
          }
        },
      },
    );
  }, [
    proposal,
    proposalEditor,
    editedName,
    editedKeywords,
    updateConcept,
    onAccepted,
    onOpenChange,
    fetchProposal,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="size-5 text-amber-500" />
            Refine Concept with AI
          </DialogTitle>
        </DialogHeader>

        {state === "loading" && <LoadingSkeleton />}

        {state === "error" && (
          <div className="flex flex-col items-center gap-4 py-8">
            <AlertCircle className="size-10 text-destructive" />
            <p className="text-sm text-muted-foreground">
              Failed to generate a refinement proposal.
            </p>
            {retryCount < MAX_RETRIES ? (
              <Button variant="outline" onClick={handleRetry}>
                Try again
              </Button>
            ) : (
              <p className="text-xs text-muted-foreground">
                Maximum retries reached. Please close and try again later.
              </p>
            )}
          </div>
        )}

        {state === "proposal" && proposal && (
          <div className="space-y-4">
            {/* Two-column comparison */}
            <div className="grid grid-cols-2 gap-4">
              {/* Left: current concept (read-only) */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-muted-foreground">
                  Current
                </h3>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Name</Label>
                  <p className="rounded-md border border-input bg-muted/50 px-3 py-2 text-sm">
                    {concept.name}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">
                    Description
                  </Label>
                  <EditorContent editor={currentEditor} />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">
                    Keywords
                  </Label>
                  <div className="flex flex-wrap gap-1.5">
                    {(concept.keywords || "")
                      .split(",")
                      .map((k) => k.trim())
                      .filter(Boolean)
                      .map((kw) => (
                        <span
                          key={kw}
                          className="rounded-full bg-muted px-2.5 py-0.5 text-xs"
                        >
                          {kw}
                        </span>
                      ))}
                    {!concept.keywords && (
                      <span className="text-xs italic text-muted-foreground">
                        No keywords
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Right: proposal (editable) */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-primary">
                  AI Proposal
                </h3>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Name</Label>
                  <Input
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    className="text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">
                    Description
                  </Label>
                  <EditorContent editor={proposalEditor} />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">
                    Keywords
                  </Label>
                  <Input
                    value={editedKeywords}
                    onChange={(e) => setEditedKeywords(e.target.value)}
                    placeholder="keyword1, keyword2, ..."
                    className="text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Rationale */}
            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-900 dark:bg-amber-950/30">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                Rationale
              </p>
              <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                {proposal.rationale}
              </p>
            </div>

            {/* CTAs */}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleAccept}
                disabled={updateConcept.isPending || !editedName.trim()}
              >
                {updateConcept.isPending ? "Applying..." : "Accept refinement"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <p className="text-center text-sm text-muted-foreground">
        Analyzing your highlights, scores, and dismissals...
      </p>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-3">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
        <div className="space-y-3">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      </div>
      <Skeleton className="h-16 w-full" />
    </div>
  );
}
